import * as React from "react"
import { type ApolloError } from "@apollo/client"
import { useCreatePasswordResetMutation } from "@digits-graphql/frontend/graphql-public"
import errorHelper from "@digits-shared/helpers/errorHelper"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { getLegalEntityId } from "src/frontend/components/Public/Shared/shared"
import { sessionError } from "src/frontend/components/Session/Glow/error"
import { useChromeAlerts } from "src/frontend/hooks/useChromeAlerts"
import { useObjectSharingState } from "src/frontend/hooks/useObjectSharingState"
import { useSharedObjectInfo } from "src/frontend/hooks/useSharedObjectInfo"
import { useSharingIntentId } from "src/frontend/hooks/useSharingIntentId"

export function usePasswordReset(onResetCompleted: () => void, onStart?: () => void) {
  const {
    state: { emailAddress },
    dispatch,
  } = useOnboardingContext()
  const { alertError } = useChromeAlerts()
  const objectSharingState = useObjectSharingState()
  const sharedObjectInfo = useSharedObjectInfo()
  const intentId = useSharingIntentId()

  const onError = React.useCallback(
    (e: ApolloError) => {
      TrackJS?.track(e)
      alertError(sessionError)
      dispatch({ type: "SET_LOADING", loading: false })
      dispatch({ type: "SET_ERROR_TEXT", text: errorHelper.errorMessage(e) })
    },
    [dispatch, alertError]
  )

  const onCompleted = React.useCallback(() => {
    dispatch({ type: "SET_LOADING", loading: false })
    onResetCompleted()
  }, [onResetCompleted, dispatch])

  const [createPasswordReset] = useCreatePasswordResetMutation({
    context: { publicAPI: true },
    onCompleted,
    onError,
  })

  const onResetPassword = React.useCallback(() => {
    // We can optionally create the reset in the context of the shared object,
    // which affects the link generated for return to the site, if we're in an
    // object sharing flow.
    const object =
      sharedObjectInfo && objectSharingState.preview
        ? {
            id: sharedObjectInfo.id,
            kind: sharedObjectInfo.kind,
            legalEntityId: getLegalEntityId(objectSharingState.preview),
          }
        : undefined

    dispatch({ type: "SET_LOADING", loading: true })
    onStart?.()
    createPasswordReset({
      variables: {
        emailAddress: emailAddress || "",
        object,
        intentId,
      },
    })
  }, [
    onStart,
    createPasswordReset,
    dispatch,
    emailAddress,
    intentId,
    objectSharingState.preview,
    sharedObjectInfo,
  ])

  return onResetPassword
}
